
import { defineComponent, getCurrentInstance, PropType, watch, ref } from 'vue';
import { KJUR, KEYUTIL, hextob64 } from 'jsrsasign';
import { kwaiOrSnack, generateUUID } from '@/common/utils';
import { sendLog } from '@/common/radar';
import { ScanApiService } from '~/api/scan';
import { UserApiService } from '~/api/user';
import { renderGoogleLoginButton, initGoogleAccountSDK } from './google';
import { initFBAccountSDK, onFacebookSign } from './facebook';
import { key } from './privateKey';

// 跳转url
const serviceUrl = 'https://www.kwai.com/legal?id=terms_service';
const privacyUrl = 'https://www.kwai.com/legal?id=privacy_policy';
type LoginOption = {
    icon: string;
    label: string;
    id?: string;
    onClick?: () => void;
};

export default defineComponent({
    name: 'login-modal',
    components: {},
    props: {
        loginModel: {
            type: Boolean,
            default: false,
        },
        iconShow: {
            type: String,
            default: 'kwai',
        },
        isScan: {
            type: Boolean,
            default: false,
        },
        containerId: {
            type: String,
            default: 'login-popup-google-button',
        },
    },
    setup(props, ctx) {
        const { proxy } = getCurrentInstance() as { proxy: any };
        const isKwaiOrSnack = kwaiOrSnack(proxy);
        const qrCode = ref<string>('');
        const isLoad = ref<boolean>(false);
        const getQrCodeError = ref<boolean>(false);
        const qrCodePassport = ref<string | null>(null);
        const expireTime = ref<number | null>(null);
        const timer = ref<number | null>(null);
        const isScanSuccess = ref<boolean>(false);
        const isScanFailed = ref<boolean>(false);
        const ScanApi = new ScanApiService();
        const UserApi = new UserApiService();

        // 扫码登陆相关参数
        const appId = isKwaiOrSnack === 'Snack' ? 'snackPcWeb' : 'kwaiPcWeb';
        const scope = 'all';

        const jump = (url: string) => {
            window.open(url);
        };

        const encryptWithPrivateKey = (data: string) => {
            const rsaKey = KEYUTIL.getKey(key);
            const sig = new KJUR.crypto.Signature({ alg: 'SHA256withRSA' });
            sig.init(rsaKey);
            sig.updateString(data);
            const signatureHex = sig.sign();
            return hextob64(signatureHex);
        };

        // 获取二维码
        const getQrCode = async () => {
            const uuid = generateUUID();
            const time = new Date().getTime();
            const signature = encryptWithPrivateKey(`appId=${appId}&scope=${scope}&nonceStr=${uuid}&timeStamp=${time}`);
            isLoad.value = true;
            const res = await ScanApi.getQrCode({
                appId,
                scope,
                nonceStr: uuid,
                timeStamp: time,
                signature,
            });
            isLoad.value = false;
            if (res && res.result === 1) {
                qrCode.value = res.qrImageUrl as string;
                qrCodePassport.value = res.qrCodePassport as string;
                expireTime.value = res.expireTime as number;
            } else {
                getQrCodeError.value = true;
            }
        };

        // 轮询状态
        const getStatus = async () => {
            if (qrCodePassport.value && expireTime.value) {
                const uuid = generateUUID() as string;
                const time = new Date().getTime();
                const signature = encryptWithPrivateKey(
                    `appId=${appId}&nonceStr=${uuid}&timeStamp=${time}&qrCodePassport=${qrCodePassport.value}&expireTime=${expireTime.value}`
                );
                const res = await ScanApi.checkState({
                    appId,
                    nonceStr: uuid,
                    timeStamp: time,
                    signature,
                    qrCodePassport: qrCodePassport.value,
                    expireTime: expireTime.value,
                });

                // 登陆成功
                if (res && res.result === 1 && res.authTokenValue) {
                    timer.value && clearInterval(timer.value);
                    isScanSuccess.value = true;
                    await proxy.$axios.$$post(`loginApi::/rest/o/w/user/login/setCookie`, {
                        authToken: res.authTokenValue,
                        sid: 'kwaipro.pwa',
                        followUrl: window?.location?.href,
                    });
                    proxy.$store.commit('setIsLogin', true);
                    const loginRes = await proxy.$axios.$$post('overseaAPI::/rest/o/w/user/login/visitor/info');
                    if (loginRes.result === 1) {
                        const { user_name, headurl } = loginRes.user;
                        proxy.$store.commit('setUserInfo', {
                            user_name,
                            headurl,
                        });
                    }
                    setTimeout(() => ctx.emit('close'), 2000);
                }
            }
        };

        // 重试
        const retry = () => {
            isScanFailed.value = false;
            getQrCode();
            if (timer.value) clearInterval(timer.value);
            timer.value = setInterval(() => {
                const now = new Date().getTime();
                if (expireTime.value && now > expireTime.value) {
                    isScanFailed.value = true;
                    timer.value && clearInterval(timer.value);
                } else getStatus();
            }, 2000) as unknown as number;
        };

        // 隐藏登陆弹窗
        const hideLoginModel = () => {
            if (timer.value) clearInterval(timer.value);
            timer.value = null;
            ctx.emit('close');
        };

        // 切换为扫码状态
        const toggleIsScan = () => {
            if (timer.value) clearInterval(timer.value);
            timer.value = null;
            getQrCodeError.value = false;
            ctx.emit('scan');
        };

        const loginConfig: Record<string, LoginOption> = {
            google: {
                icon: 'https://cdn-static.kwai.net/kos/s101/nlav11312/kos/s101/nlav11312/pwa/pc/google_icon.png',
                label: 'k_1241455',
                id: props.containerId,
            },
            qrCode: {
                icon: 'https://cdn-static.kwai.net/kos/s101/nlav11312/kos/s101/nlav11312/pwa/pc/scan_icon.png',
                label: 'k_1241456',
                onClick: toggleIsScan,
            },
            facebook: {
                icon: 'https://cdn-static.kwai.net/kos/s101/nlav11312/kos/s101/nlav11312/pwa/pc/facebook_icon.png',
                label: 'k_1245814',
                onClick: () => onFacebookSign(proxy, hideLoginModel, UserApi),
            },
        };

        watch(
            [() => props.loginModel, () => props.isScan],
            ([newLoginModel]) => {
                if (newLoginModel && !props.isScan) {
                    const isLoadedGoogleScript = !!(window as any)?.google;
                    const isLoadedFBScript = !!(window as any)?.FB;
                    if (!isLoadedFBScript) initFBAccountSDK();
                    if (isLoadedGoogleScript) {
                        setTimeout(() => {
                            renderGoogleLoginButton(hideLoginModel, props.containerId, proxy, UserApi);
                        }, 0);
                        return;
                    }
                    initGoogleAccountSDK(hideLoginModel, props.containerId, proxy, UserApi);
                } else if (newLoginModel && props.isScan) {
                    const now = new Date().getTime();
                    // 过期 或 超时重发请求获取QrCode
                    if (!qrCode.value || (expireTime.value && now > expireTime.value)) {
                        getQrCode();
                    }
                    if (!timer.value) {
                        timer.value = setInterval(async () => {
                            const now = new Date().getTime();
                            if (expireTime.value && now > expireTime.value) {
                                isScanFailed.value = true;
                                timer.value && clearInterval(timer.value);
                            } else getStatus();
                        }, 2000) as unknown as number;
                    }
                }
            },
            { immediate: true }
        );

        return {
            proxy,
            isKwaiOrSnack,
            isScanSuccess,
            isScanFailed,
            qrCode,
            isLoad,
            getQrCodeError,
            serviceUrl,
            privacyUrl,
            loginConfig,
            onFacebookSign,
            hideLoginModel,
            toggleIsScan,
            retry,
            jump,
        };
    },
});
